/**
 * The rich text editor's schema whitelist, and the guard that decides whether a piece
 * of email HTML may be handed to it at all.
 *
 * CommonJS with a plain-node test (tiptapConfig.test.js) because this admin has no Jest,
 * no Vitest and no RTL — the only testable place for logic is src/utils/.
 *
 * WHY THE GUARD EXISTS. Tiptap's schema is a whitelist: anything it does not recognise is
 * silently dropped the first time the document round-trips through the editor. Point it
 * at markup it does not model and that markup is gone — not as a bug, but as the
 * documented behaviour of the thing. So something has to decide, per cell, whether this
 * editor can hold this cell without changing it. That is what this is.
 *
 * WHAT CHANGED, AND WHY IT HAD TO. The first version of this guard refused any cell
 * containing `style=`, because Tiptap kept no style attribute. That is every rich cell
 * there is: measured on codigo-fan-sale-es, all seven carry inline typography. So every
 * one of them fell through to the text-run editor, which shows a paragraph as one field
 * per fragment between tags — five fields for one legal paragraph, and an edit landing in
 * the wrong fragment. The editor was worse than the raw HTML it replaced.
 *
 * The answer was not a better fallback. It was to make Tiptap model what these emails
 * contain — see tiptapExtensions.js — and then to relax this guard to match what that
 * change actually PROVED, which is what tiptapRoundTrip.test.js measures over the real
 * cells rather than over inventions.
 *
 * WHAT IS STILL REFUSED, and why each one:
 *
 *   <table>           layout, not copy; Tiptap has no table node here.
 *   <!--[if …]-->     Outlook conditionals; Tiptap has no node for a comment at all.
 *   <script|style|…>  never handed to a contenteditable.
 *   <img>             see NOTE ON IMAGES below.
 *   <b …>/<i …>       a b or an i CARRYING ATTRIBUTES — see NOTE ON <b> AND <i> below.
 *   any other tag     u, font, div-inside-the-body, o:p — each of them either vanishes
 *                     or is rewritten, and neither has been proved here.
 *   loose top level   text or an inline element with no block around it. A ProseMirror
 *                     document is a list of blocks; hand it a bare run of words and it
 *                     invents a <p> that was never in the email. That is why a cell that
 *                     is nothing but a styled <a> button keeps the text-run editor.
 *                     Conversion now gives a text block's loose copy a <p style="margin:0">
 *                     of its own (emailHtmlToBlocks.js) so that this is not the common
 *                     case it used to be; the refusal stays for everything else.
 *
 * NOTE ON <b> AND <i>. Admitted, and this is the one rewrite this guard knowingly allows.
 * A bare `<b>` comes back out of the editor as `<strong>`, and a bare `<i>` as `<em>` —
 * the same text, the same nesting, one element renamed to its semantic twin. Every email
 * client renders the pair identically, and tiptapRoundTrip.test.js proves the rename is
 * the ONLY difference, on the real strings out of the PRO templates. It is still a change
 * to the stored document: the first save of such a block rewrites `<b>` to `<strong>`,
 * once, and nothing about the sent email looks different afterwards.
 *
 * With ATTRIBUTES it is refused, because then the rename is not the only change. The bold
 * and italic marks carry no attributes at all, so a `<b style="color:#c00">` would come
 * back as a plain `<strong>` with the colour gone. Nothing in this corpus writes one —
 * all 26 `<b>`s in it are bare, and there is no `<i>`, `<u>` or `<font>` anywhere — so
 * this costs nothing today and is the difference between a proved rewrite and a guess.
 *
 * NOTE ON IMAGES. Still refused, deliberately. There is no Image node in this schema, so
 * an <img> is dropped outright — but even adding one would not settle it: an email <img>
 * carries width, height, border, align, hspace, style and alt, StarterKit's image node
 * models three of those, and an image in a cell is almost always a logo or a spacer shim
 * rather than copy anyone wants to edit. Refusing costs the user the HTML editor for
 * those cells. Admitting it, unproved, would cost a stripped logo in a sent email.
 *
 * A false result is not an error. It means the cell keeps one of the other two controls,
 * which is exactly what it had before.
 */

const { topLevelNodes, unwrapChrome } = require('./emailCellChrome');

// StarterKit nodes we keep, plus the one node this feature adds. Everything absent here
// is disabled explicitly at configuration time rather than left to Tiptap's defaults, so
// a Tiptap upgrade that adds a node cannot silently widen what the editor may write into
// an email.
const ALLOWED_NODES = [
  'doc',
  'paragraph',
  'text',
  'heading',
  'bulletList',
  'orderedList',
  'listItem',
  'hardBreak',
  // The email's own call-to-action anchor, round-tripped rather than approximated.
  'emailButton',
];

// 'emailSpan' is the <span style="…"> / <span class="tinyMce-placeholder"> run that this
// copy is full of, kept as a mark so its class and style survive the round trip.
const ALLOWED_MARKS = ['bold', 'italic', 'link', 'emailSpan'];

// Tags the schema models, anywhere inside the body. <b> and <i> are in the list and come
// back renamed to <strong> and <em>; see NOTE ON <b> AND <i> above for why that rename is
// admitted and an attributed one is not. <u> and <font> are still absent: <u> has no mark
// at all and would simply vanish, and neither appears in any template that was measured.
const RICH_TAGS = new Set([
  'p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'ul', 'ol', 'li',
  'a', 'strong', 'em', 'b', 'i', 'span', 'br',
]);

// A `<b>` or an `<i>` that carries anything at all. `<br…>` and `<i…>`-prefixed names
// like `<img>` cannot match: the character after the tag name must be whitespace, which
// is what ends a tag name.
const ATTRIBUTED_B_OR_I = /<(b|i)\s[^>]*>/i;

// What may sit at the top level of the body once the typography wrapper is peeled off.
const TOP_LEVEL_BLOCKS = new Set(['p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'ul', 'ol']);

const TAG_NAME_PATTERN = /<\/?([a-zA-Z][a-zA-Z0-9:-]*)/g;

/**
 * Would the rich text editor change this cell?
 *
 * Called with the WHOLE cell, wrapper and all: the typography wrapper is peeled off here
 * (emailCellChrome) exactly as EmailCellEditor peels it, so the guard judges the same
 * string the editor is actually given and not one byte more.
 *
 * String-based rather than DOM-based on purpose: this runs in the browser before mount
 * and again on every keystroke, a false negative silently corrupts a production email,
 * and a false positive merely leaves the user with the editor they had yesterday.
 */
function isContentSafeForRichText(html) {
  if (!html) return true;

  // Layout. A cell holding a nested table is chrome, not copy.
  if (/<table\b/i.test(html)) return false;

  // Comments of every kind, MSO conditionals included — Tiptap has no node for one.
  if (/<!--/.test(html)) return false;

  // Never hand these to a contenteditable.
  if (/<(script|style|iframe|object|embed)\b/i.test(html)) return false;

  // See NOTE ON IMAGES above.
  if (/<img\b/i.test(html)) return false;

  // See NOTE ON <b> AND <i> above: the rename is proved, keeping an attribute is not.
  if (ATTRIBUTED_B_OR_I.test(html)) return false;

  const { inner } = unwrapChrome(html);
  if (!inner.trim()) return true;

  const nodes = topLevelNodes(inner);
  // null means the scanner could not account for the markup — unbalanced tags, a stray
  // close. "I do not understand this" is a refusal, never a guess.
  if (nodes === null) return false;

  let blocks = 0;
  for (let index = 0; index < nodes.length; index += 1) {
    const node = nodes[index];
    if (node.type === 'text') {
      // Whitespace between blocks is fine and is preserved as chrome. Words are not:
      // ProseMirror would wrap them in a <p> the email never had.
      if (inner.slice(node.start, node.end).trim()) return false;
    } else if (node.type !== 'element' || !TOP_LEVEL_BLOCKS.has(node.name)) {
      return false;
    } else {
      blocks += 1;
    }
  }
  if (blocks === 0) return false;

  TAG_NAME_PATTERN.lastIndex = 0;
  let match = TAG_NAME_PATTERN.exec(inner);
  while (match !== null) {
    if (!RICH_TAGS.has(match[1].toLowerCase())) return false;
    match = TAG_NAME_PATTERN.exec(inner);
  }

  return true;
}

module.exports = {
  ALLOWED_NODES, ALLOWED_MARKS, RICH_TAGS, TOP_LEVEL_BLOCKS, isContentSafeForRichText,
};
